<script setup lang="ts">
import type { Law } from '~/utils/solr/law'
import { faLockKeyhole, faCircleInfo } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<{
  law: Law
}>()

const title = computed(() => props.law.shortTitleWithoutAbbreviation ?? props.law.title)
const { isFavorite, toggleFavorite, isFavoriteEnabled } = useFavorites()

const hasRecentCommentary = computed(() => {
  if (!props.law.lastUpdatedByEditor) {
    return false
  }
  const lastUpdatedDate = new Date(props.law.lastUpdatedByEditor)
  const now = new Date()
  const diffInDays = (now.getTime() - lastUpdatedDate.getTime()) / (1000 * 60 * 60 * 24)
  return diffInDays <= 30
})
</script>

<template>
  <LawLink
    :law="law"
    class="flex items-center w-full no-underline py-2xs px-xs h-[4.625rem] rounded border-2 border-surface-primary bg-surface-primary hover:bg-surface-secondary hover:border-surface-secondary active:bg-blurple-200 active:border-blurple-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blurple-300"
    :aria-label="title"
  >
    <div class="flex flex-col grow gap-3xs">
      <h3 class="font-bold text-small">{{ title }}</h3>
      <div class="flex items-center gap-2xs">
        <span v-if="!law.has_commentary" class="text-caption text-secondary">
          Uten lovkommentar
        </span>
        <div v-if="law.isRepealed" class="inline-flex items-center text-caption gap-3xs text-secondary shrink-0">
          <Icon class="-translate-y-px" :icon="faCircleInfo" />
          <span>Opphevet</span>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-2xs">
      <Badge v-if="hasRecentCommentary" variant="new" size="small" class="flex items-center text-caption gap-3xs text-secondary shrink-0" />
      <FavoriteButton v-if="isFavoriteEnabled" intent="outlined" :selected="isFavorite('law', law.id)" :aria-label="isFavorite('law', law.id) ? `Fjern ${title} fra favoritter` : `Legg til ${title} i favoritter`" @click.prevent="toggleFavorite('law', law.id)" />
      <Icon v-else :icon="faLockKeyhole" />
    </div>
  </LawLink>
</template>
